import debounce from "lodash/debounce";
import catalog from "@granular/fabric-catalog";

import activities from "../../../redux/activities";
import { FetcherQueryKey, QueryFunction } from "../../../services";
import { User } from "../../../models";
import { getAuthToken } from "../../../services/granularAxios";

import { useSetUserGlobalActivityAndStatus } from "./user.globalStatus";

export type UserFetcherParams = {
  userId: string | undefined;
};
export type UserQueryKey = FetcherQueryKey<UserFetcherParams>;

export const userQueryKeyPrefix = "[@fabric/core][user]";

/**
 * Computes the userQueryKey from the given params.
 */
export const computeUserQueryKey = (
  params: UserFetcherParams,
): UserQueryKey => [userQueryKeyPrefix, params];

/**
 * Fetches the user using the catalog.
 */
export const userFetcher: QueryFunction<User, UserFetcherParams> = () => {
  const catalogConfig = {
    // IMPORTANT: We are not passing the authToken as part of the queryKey
    // because we don't want to invalidate our data on auth token change.
    // The auth token can and will change throughout the lifetime of the app,
    // but that should not invalidate our data.
    getAuthToken,
  };
  return catalog.user
    .getUser({ userRequestConfig: catalogConfig })
    .then((r) => r.data);
};

/**
 * The returned value of this will contain
 * the React Query onSuccess function for useUserQuery
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useUserQuery.
 *
 * This is only meant for use in useUserQuery.
 */
export const onSuccess = debounce(
  ({
    setUserGlobalActivityAndStatus,
  }: {
    setUserGlobalActivityAndStatus: ReturnType<
      typeof useSetUserGlobalActivityAndStatus
    >;
  }) => {
    setUserGlobalActivityAndStatus({
      status: "success",
      activity: activities.getUser,
    });
  },
  0,
);

/**
 * The returned value of this will contain
 * the React Query onError function for useUserQuery.
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useUserQuery.
 *
 * This is only meant for use in useUserQuery.
 */
export const onError = debounce(
  ({
    setUserGlobalActivityAndStatus,
  }: {
    setUserGlobalActivityAndStatus: ReturnType<
      typeof useSetUserGlobalActivityAndStatus
    >;
  }) => {
    setUserGlobalActivityAndStatus({
      status: "error",
      activity: activities.getUser,
    });
  },
  0,
);

/**
 * The returned value of this will contain
 * the function to call when loading in useUserQuery.
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useUserQuery.
 *
 * This is only meant for use in useUserQuery.
 */
export const onLoading = debounce(
  ({
    setUserGlobalActivityAndStatus,
  }: {
    setUserGlobalActivityAndStatus: ReturnType<
      typeof useSetUserGlobalActivityAndStatus
    >;
  }) => {
    setUserGlobalActivityAndStatus({
      status: "loading",
      activity: activities.getUser,
    });
  },
  0,
);

/**
 * The returned value of this will contain
 * the React Query isDataEqual function for useUserQuery.
 *
 * This is run once per network response as opposed to once per hook instance.
 *
 * This is only meant for use in useUserQuery.
 */
export const isDataEqual = (
  oldUser: User | undefined,
  newUser: User,
): boolean => {
  if (!oldUser) {
    return false;
  }
  return oldUser.lastModified === newUser.lastModified;
};
